import React from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  ChevronRight,
  MessageCircle,
  Stethoscope,
  ClipboardCheck,
} from "lucide-react";
import { CONTACT_LINKS } from "@/lib/site-data";

const STEPS = [
  {
    id: "step-1",
    title: "You reach out",
    body: "WhatsApp or live chatwhichever you already use. Tell us what’s going on in your own words.",
    Icon: MessageCircle,
    iconBg: "bg-forest/12",
    iconColor: "text-forest",
    dotClass: "bg-forest",
  },
  {
    id: "step-2",
    title: "A doctor joins you",
    body: "A licensed clinician replies by text, voice, or videousually in minutes, not days.",
    Icon: Stethoscope,
    iconBg: "bg-clay/15",
    iconColor: "text-clay",
    dotClass: "bg-clay",
  },
  {
    id: "step-3",
    title: "You get a clear plan",
    body: "Care advice, prescriptions when appropriate, and follow-upseverything saved to one record.",
    Icon: ClipboardCheck,
    iconBg: "bg-forest/12",
    iconColor: "text-forest",
    dotClass: "bg-forest",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
};

export default function HowItWorks() {
  return (
    <section
      id="how-it-works"
      data-testid="how-it-works"
      className="scroll-mt-[88px] relative overflow-hidden bg-cream"
    >
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_-20%,rgba(22,78,64,0.08),transparent_55%)]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute bottom-0 left-1/2 h-px w-[min(100%,720px)] -translate-x-1/2 bg-gradient-to-r from-transparent via-ink/15 to-transparent"
      />

      <div className="container-x relative py-20 lg:py-28">
        <motion.header
          {...fadeUp}
          className="mx-auto max-w-2xl text-center"
        >
          <p className="text-[12.5px] font-semibold uppercase tracking-[0.18em] text-forest">
            Simple by design
          </p>
          <h2 className="mt-4 font-display text-ink text-[32px] sm:text-[42px] lg:text-[48px] font-extrabold leading-[1.08] tracking-tight text-balance">
            How getting care works
          </h2>
          <p className="mt-5 text-[17px] text-ink/65 leading-relaxed">
            One straight path from your first message to a real clinicianno
            waiting room, no new app to figure out first.
          </p>
        </motion.header>

        {/* Desktop: horizontal journey */}
        <div className="mt-16 hidden lg:flex lg:items-stretch lg:justify-center lg:gap-0">
          {STEPS.map((step, index) => (
            <React.Fragment key={step.id}>
              <motion.article
                {...fadeUp}
                transition={{
                  ...fadeUp.transition,
                  delay: index * 0.08,
                }}
                className="relative flex w-[min(100%,280px)] flex-col rounded-3xl border border-ink/10 bg-white p-8 shadow-[0_12px_40px_-12px_rgba(14,35,51,0.12)]"
              >
                <div
                  className="absolute -top-3 left-8 inline-flex rounded-full bg-ink px-3 py-1 text-[11px] font-bold uppercase tracking-[0.14em] text-cream"
                  aria-hidden="true"
                >
                  Step {index + 1}
                </div>
                <div
                  className={`mt-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl ${step.iconBg}`}
                >
                  <step.Icon
                    className={`h-7 w-7 ${step.iconColor}`}
                    strokeWidth={1.75}
                    aria-hidden="true"
                  />
                </div>
                <h3 className="mt-6 font-display text-[20px] font-bold text-ink leading-snug">
                  {step.title}
                </h3>
                <p className="mt-3 text-[15px] text-ink/68 leading-relaxed">
                  {step.body}
                </p>
              </motion.article>

              {index < STEPS.length - 1 && (
                <div
                  className="flex w-14 shrink-0 items-center justify-center self-center"
                  aria-hidden="true"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full border border-ink/10 bg-cream shadow-sm">
                    <ChevronRight className="h-5 w-5 text-clay" strokeWidth={2} />
                  </div>
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Mobile / tablet: vertical timeline */}
        <div className="mt-14 lg:hidden">
          <div className="relative mx-auto max-w-md">
            <div
              className="absolute left-[19px] top-8 bottom-8 w-0.5 bg-gradient-to-b from-forest/35 via-clay/40 to-forest/35"
              aria-hidden="true"
            />
            <ol className="relative space-y-10">
              {STEPS.map((step, index) => (
                <motion.li
                  key={step.id}
                  {...fadeUp}
                  transition={{
                    ...fadeUp.transition,
                    delay: index * 0.06,
                  }}
                  className="flex gap-5"
                >
                  <div className="relative z-[1] flex shrink-0 flex-col items-center">
                    <span
                      className={`flex h-10 w-10 items-center justify-center rounded-full ring-4 ring-cream ${step.dotClass} text-cream shadow-md`}
                    >
                      <step.Icon className="h-5 w-5" strokeWidth={2} aria-hidden="true" />
                    </span>
                  </div>
                  <div className="min-w-0 flex-1 rounded-2xl border border-ink/10 bg-white px-5 py-5 shadow-sm">
                    <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-ink/45">
                      Step {index + 1}
                    </p>
                    <h3 className="mt-2 font-display text-[18px] font-bold text-ink">
                      {step.title}
                    </h3>
                    <p className="mt-2 text-[15px] text-ink/68 leading-relaxed">
                      {step.body}
                    </p>
                  </div>
                </motion.li>
              ))}
            </ol>
          </div>
        </div>

        {/* CTA */}
        <motion.div
          {...fadeUp}
          transition={{ ...fadeUp.transition, delay: 0.2 }}
          className="mx-auto mt-16 max-w-2xl rounded-3xl border border-forest/20 bg-forest/[0.06] px-8 py-10 text-center lg:mt-20"
        >
          <p className="font-display text-[22px] sm:text-[26px] font-bold text-ink leading-snug">
            Ready to start?
          </p>
          <p className="mt-2 text-[15px] text-ink/65">
            Jump to care in one tapsame flow you just saw.
          </p>
          <a
            href={CONTACT_LINKS.whatsapp.href}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="how-it-works-cta"
            className="mt-8 inline-flex items-center justify-center gap-2 rounded-full bg-forest hover:bg-forestHover text-cream px-10 py-4 text-[16px] font-semibold shadow-lg shadow-forest/20 hover:shadow-xl hover:-translate-y-0.5 transition-all"
          >
            Get care now
            <MessageCircle className="h-5 w-5" aria-hidden="true" />
          </a>
        </motion.div>
      </div>
    </section>
  );
}
